import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setManualOffline } from '../../app/EditorSlice';
import { setManualOfflineMode } from '../../utils/helper';

/**
 * Shared offline-toggle behaviour.
 *
 * Keeps the localStorage-backed manual offline flag and its redux mirror in sync,
 * and tracks the browser's real connectivity so consumers can show the *effective*
 * offline state. Used by both the header pill (OfflineToggle) and the mobile
 * hamburger menu item.
 */
export function useOfflineToggle() {
  const dispatch = useDispatch();
  const isManualOffline = useSelector((state) => state.editor.isManualOffline);
  const [isBrowserOnline, setIsBrowserOnline] = useState(window.navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsBrowserOnline(true);
    const handleOffline = () => setIsBrowserOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const offline = isManualOffline || !isBrowserOnline;

  const toggle = () => {
    // No network means we can't go back online — leave the manual intent untouched.
    if (!isBrowserOnline) return;
    const next = !isManualOffline;
    setManualOfflineMode(next);
    dispatch(setManualOffline(next));
    if (!next && window.navigator.onLine) {
      window.dispatchEvent(new Event('online'));
    }
  };

  return { offline, isBrowserOnline, isManualOffline, toggle };
}
